<script>
  import { onMount } from "svelte";
  import { formatUnixTimestamp } from "../../services/utilities.js";
  import { getEvents } from "../../models/events.js";

  let futureEvents = [];
  let pastEvents = [];
  let now = new Date().getTime() / 1000;

  onMount(async () => {
    let events = await getEvents();
    futureEvents = events.filter((e) => e.start_ts > now);
    pastEvents = events
      .filter((e) => e.end_ts <= now)
      .sort((a, b) => b.end_ts - a.end_ts)
      .slice(0, 10);

    if (futureEvents.length > 0) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: futureEvents.map((e, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "Event",
            name: e.title,
            startDate: new Date(e.start_ts * 1000).toISOString(),
            endDate: new Date(e.end_ts * 1000).toISOString(),
            location: e.virtual
              ? {
                  "@type": "VirtualLocation",
                  url: "https://www.puzzleexchange.com/events",
                }
              : { "@type": "Place", name: e.location },
            url: `https://www.puzzleexchange.com/event/${e.id}`,
            organizer: {
              "@type": "Organization",
              name: "Missing Piece Puzzle Exchange",
              url: "https://www.puzzleexchange.com",
            },
          },
        })),
      };
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    }
  });
</script>

<svelte:head>
  <title>Puzzle Events in Colorado | Missing Piece Puzzle Exchange</title>
  <meta
    name="description"
    content="Join Missing Piece Puzzle Exchange for puzzle competitions and events in the Denver area. Check upcoming events and buy tickets."
  />
  <meta
    property="og:title"
    content="Puzzle Events in Colorado | Missing Piece Puzzle Exchange"
  />
  <meta
    property="og:description"
    content="Join Missing Piece Puzzle Exchange for puzzle competitions and events in the Denver area. Check upcoming events and buy tickets."
  />
  <meta property="og:url" content="https://www.puzzleexchange.com/events" />
  <meta property="og:image" content="https://images.puzzleexchange.com/home/rent-11-2024-lg.jpg" />
  <link rel="canonical" href="https://www.puzzleexchange.com/events" />
</svelte:head>

<div class="container">
  <div class="page-header">
    <h1>Puzzle Events</h1>
    <p class="subtitle">
      Puzzle competitions and community events in the Denver area.
    </p>
  </div>

  <section>
    <h2 class="section-heading">Upcoming Events</h2>

    {#if futureEvents.length === 0}
      <div class="empty-state">
        <p>No events scheduled right now — check back soon!</p>
        <p class="host-cta">
          Interested in hosting a puzzle competition at your restaurant or
          brewery? <a href="mailto:info@puzzleexchange.com">Get in touch.</a>
        </p>
      </div>
    {:else}
      <ul class="event-list">
        {#each futureEvents as e}
          <li class="event-card">
            {#if e.requires_tickets && e.purchased_quantity >= e.quantity}
              <span class="badge sold-out">Sold Out</span>
            {:else if e.requires_tickets}
              <span class="badge tickets">Tickets Required</span>
            {/if}
            <h3 class="event-title">{e.title}</h3>
            <p class="event-date">
              {formatUnixTimestamp(e.start_ts)} – {formatUnixTimestamp(
                e.end_ts,
              )}
            </p>
            <p class="event-location">
              <strong>Location{e.virtual ? " (Virtual)" : ""}:</strong>
              {e.location}
            </p>
            {#if e.requires_tickets && e.purchased_quantity < e.quantity}
              {#if e.quantity - e.purchased_quantity < 5}
                <p class="tickets-remaining">Less than 5 tickets remaining</p>
              {:else if e.enrollment_start_ts <= now && e.enrollment_end_ts > now}
                <p class="tickets-remaining">Tickets available</p>
              {:else if e.enrollment_end_ts <= now}
                <p class="tickets-closed">
                  Ticket sales have closed for this event.
                </p>
              {/if}
            {/if}
            <a class="btn event-btn" href={`/event/${e.id}`}>
              {e.requires_tickets &&
              e.purchased_quantity < e.quantity &&
              e.enrollment_start_ts <= now &&
              e.enrollment_end_ts > now
                ? "Read More & Buy Tickets"
                : "Read More"}
            </a>
          </li>
        {/each}
      </ul>
      <p class="host-cta">
        Interested in hosting a puzzle competition at your restaurant or
        brewery? <a href="mailto:info@puzzleexchange.com">Get in touch.</a>
      </p>
    {/if}
  </section>

  {#if pastEvents.length > 0}
    <section class="past-section">
      <h2 class="section-heading">Past Events</h2>
      <ul class="event-list past">
        {#each pastEvents as e}
          <li class="event-card past-card">
            <h3 class="event-title">{e.title}</h3>
            <p class="event-date">
              {formatUnixTimestamp(e.start_ts)} – {formatUnixTimestamp(
                e.end_ts,
              )}
            </p>
            <p class="event-location">
              <strong>Location{e.virtual ? " (Virtual)" : ""}:</strong>
              {e.location}
            </p>
            <a class="view-results" href={`/event/${e.id}`}>View Results →</a>
          </li>
        {/each}
      </ul>
    </section>
  {/if}
</div>

<style type="text/less">
  .container {
    max-width: 800px;
    margin: 2rem auto;
    padding: 0 var(--spacing);
  }

  .page-header {
    margin-bottom: var(--spacing-lg);

    h1 {
      margin: 0 0 0.5rem;
    }
  }

  .subtitle {
    color: var(--col-gray-50);
    margin: 0;
  }

  .section-heading {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--col-gray-50);
    margin: 0 0 var(--spacing);
  }

  .event-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .event-card {
    padding: var(--spacing-lg);
    border: solid 1px var(--col-gray-90);
    background: var(--col-gray-98);
    border-radius: var(--border-radius);
    margin-bottom: var(--spacing);
    position: relative;
  }

  .event-title {
    margin: 0 0 var(--spacing-sm);
    font-size: 1.2rem;
  }

  .event-date {
    margin: 0 0 0.25rem;
    font-weight: 600;
    color: var(--col-text);
  }

  .event-location {
    margin: 0 0 var(--spacing-sm);
    color: var(--col-gray-40);
    font-size: 0.9rem;
  }

  .tickets-remaining {
    margin: 0 0 var(--spacing-sm);
    font-size: 0.9rem;
    color: var(--col-accent-1);
    font-weight: 600;
  }

  .tickets-closed {
    margin: 0 0 var(--spacing-sm);
    font-size: 0.9rem;
    color: var(--col-gray-50);
  }

  .event-btn {
    margin-top: var(--spacing-sm);
  }

  .badge {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.2em 0.6em;
    border-radius: 3px;
    margin-bottom: var(--spacing-sm);
  }

  .badge.sold-out {
    background: var(--col-gray-80);
    color: var(--col-gray-40);
  }

  .badge.tickets {
    background: var(--col-accent-1);
    color: white;
  }

  .empty-state {
    padding: var(--spacing-lg);
    border: solid 1px var(--col-gray-90);
    background: var(--col-gray-98);
    border-radius: var(--border-radius);
    color: var(--col-gray-50);
  }

  .host-cta {
    font-size: 0.9rem;
    color: var(--col-gray-50);
    margin-top: var(--spacing);
  }

  .past-section {
    margin-top: var(--spacing-lg);
    padding-top: var(--spacing-lg);
    border-top: solid 1px var(--col-gray-90);
  }

  .past-card {
    background: transparent;
    border-color: var(--col-gray-90);

    .event-title {
      font-size: 1rem;
      color: var(--col-gray-40);
    }

    .event-date {
      font-weight: 400;
      font-size: 0.9rem;
      color: var(--col-gray-50);
    }
  }

  .view-results {
    font-size: 0.9rem;

    &:link,
    &:visited {
      color: var(--col-gray-40);
      text-decoration: none;
    }

    &:hover,
    &:focus {
      color: var(--col-text);
    }
  }
</style>
